import { formatDate, formatIdr, formatUsd, type ApiKey } from '@ytclip/shared'
import { Infinity as InfinityIcon, KeyRound, Pencil, Plus, Trash2, Wallet } from 'lucide-react'
import { useState } from 'react'
import { Button, IconButton } from '@/components/ui/button.tsx'
import { Card, CardBody, CardHeader } from '@/components/ui/card.tsx'
import { ConfirmDialog } from '@/components/ui/dialog.tsx'
import { Badge, EmptyState, ErrorState, Skeleton } from '@/components/ui/feedback.tsx'
import { PageHeader } from '@/components/ui/misc.tsx'
import { TopupDialog } from '@/features/billing/topup-dialog.tsx'
import { CreateKeyDialog, EditKeyDialog } from '@/features/keys/key-dialogs.tsx'
import { useApiKeys, useDeleteApiKey } from '@/features/keys/hooks.ts'
import { useBalance } from '@/features/shared-hooks.ts'

export function DashboardPage() {
  const [topupOpen, setTopupOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<ApiKey | null>(null)
  const [deleting, setDeleting] = useState<ApiKey | null>(null)

  const deleteKey = useDeleteApiKey()

  return (
    <>
      <PageHeader title="Dashboard" description="Kelola saldo dan API key kamu" />

      <BalanceCard onTopup={() => setTopupOpen(true)} />

      <ApiKeysCard
        onCreate={() => setCreateOpen(true)}
        onEdit={setEditing}
        onDelete={setDeleting}
      />

      <TopupDialog open={topupOpen} onOpenChange={setTopupOpen} />
      <CreateKeyDialog open={createOpen} onOpenChange={setCreateOpen} />
      <EditKeyDialog apiKey={editing} onOpenChange={() => setEditing(null)} />

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={() => setDeleting(null)}
        title="Hapus API key?"
        description="Tindakan ini permanen. Aplikasi yang masih memakai key ini akan langsung berhenti bekerja."
        confirmLabel="Hapus"
        destructive
        loading={deleteKey.isPending}
        onConfirm={async () => {
          if (!deleting) return
          await deleteKey.mutateAsync(deleting.token)
          setDeleting(null)
        }}
      />
    </>
  )
}

function BalanceCard({ onTopup }: { onTopup: () => void }) {
  const { data, isPending, isError } = useBalance()

  return (
    <Card>
      <CardHeader title="Saldo" icon={<Wallet className="size-5" />} />
      <CardBody className="pt-4">
        {isError ? (
          <ErrorState message="Saldo tidak bisa dimuat. Coba muat ulang halaman." />
        ) : (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              {isPending ? (
                <>
                  <Skeleton className="h-12 w-40" />
                  <Skeleton className="mt-2 h-4 w-32" />
                </>
              ) : (
                <>
                  <p className="text-display-lg font-medium leading-none text-on-surface">
                    {formatUsd(data.balanceUsd)}
                  </p>
                  <p className="mt-2 text-body-md text-on-surface-variant">
                    ≈ {formatIdr(data.balanceIdr)} · terpakai {formatUsd(data.spentUsd)}
                  </p>
                </>
              )}
            </div>

            <Button size="lg" onClick={onTopup} icon={<Plus className="size-4" />}>
              Top-up saldo
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

function ApiKeysCard({
  onCreate,
  onEdit,
  onDelete,
}: {
  onCreate: () => void
  onEdit: (key: ApiKey) => void
  onDelete: (key: ApiKey) => void
}) {
  const { data, isPending, isError } = useApiKeys()

  return (
    <Card>
      <CardHeader
        title="API keys"
        icon={<KeyRound className="size-5" />}
        action={
          <Button variant="tonal" onClick={onCreate} icon={<Plus className="size-4" />}>
            Buat key
          </Button>
        }
      />
      <CardBody className="pt-4">
        {isPending ? (
          <div className="space-y-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : isError ? (
          <ErrorState message="Daftar API key tidak bisa dimuat." />
        ) : data.length === 0 ? (
          <EmptyState
            icon={<KeyRound />}
            title="Belum ada API key"
            description="Buat key pertamamu untuk mulai memanggil API."
            action={<Button onClick={onCreate}>Buat key</Button>}
          />
        ) : (
          <ul className="space-y-3">
            {data.map((apiKey) => (
              <KeyRow key={apiKey.token} apiKey={apiKey} onEdit={onEdit} onDelete={onDelete} />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  )
}

function KeyRow({
  apiKey,
  onEdit,
  onDelete,
}: {
  apiKey: ApiKey
  onEdit: (key: ApiKey) => void
  onDelete: (key: ApiKey) => void
}) {
  return (
    <li className="flex flex-col gap-4 rounded-md bg-surface-container-low p-4 transition-colors duration-300 hover:bg-surface-container-high lg:flex-row lg:items-center lg:justify-between">
      <div className="min-w-0">
        <p className="truncate text-body-md font-medium text-on-surface">{apiKey.name}</p>
        <code className="mt-0.5 block truncate font-mono text-label-sm text-on-surface-variant">
          {apiKey.maskedKey}
        </code>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-label-sm text-on-surface-variant">
          <span>Dibuat {formatDate(apiKey.createdAt)}</span>
          <span aria-hidden="true">·</span>
          <span>Terpakai {formatUsd(apiKey.spendUsd)}</span>
          <Badge tone={apiKey.maxBudgetUsd ? 'primary' : 'neutral'}>
            {apiKey.maxBudgetUsd ? (
              `Batas ${formatUsd(apiKey.maxBudgetUsd)}`
            ) : (
              <>
                <InfinityIcon className="size-3" aria-hidden="true" /> Tanpa batas
              </>
            )}
          </Badge>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <IconButton label={`Ubah ${apiKey.name}`} onClick={() => onEdit(apiKey)}>
          <Pencil className="size-4" />
        </IconButton>
        <IconButton label={`Hapus ${apiKey.name}`} tone="danger" onClick={() => onDelete(apiKey)}>
          <Trash2 className="size-4" />
        </IconButton>
      </div>
    </li>
  )
}
